import React from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  Activity,
  AlertCircle,
  Settings,
  Home,
  Sparkles,
  LogOut,
  Key,
} from "lucide-react";
import { useAuth } from "../context/AuthContext";

function Layout({ children }) {
  const location = useLocation();
  const navigate = useNavigate();
  const { logout } = useAuth();

  const navItems = [
    { path: "/dashboard", icon: Home, label: "Dashboard" },
    { path: "/errors", icon: AlertCircle, label: "Errors" },
    { path: "/api-keys", icon: Key, label: "API Keys" },
    { path: "/performance", icon: Sparkles, label: "Performance" },
    { path: "/settings", icon: Settings, label: "Settings" },
  ];

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-950 via-gray-900 to-gray-950 flex">
      {/* Sidebar */}
      <div className="w-64 bg-gradient-to-b from-gray-900/90 to-gray-950/90 backdrop-blur-sm border-r border-gray-800/50 p-4 flex flex-col">
        {/* Logo */}
        <div className="flex items-center gap-2 mb-8 px-2">
          <div className="relative">
            <div className="absolute inset-0 bg-gradient-to-r from-cyan-500 to-blue-600 rounded-lg blur-lg opacity-50 animate-pulse" />
            <Activity className="w-8 h-8 text-cyan-400 relative" />
          </div>
          <span className="text-xl font-bold bg-gradient-to-r from-cyan-400 to-blue-500 bg-clip-text text-transparent">
            FrankTech
          </span>
          <Sparkles className="w-3 h-3 text-cyan-400 ml-auto" />
        </div>

        {/* Navigation */}
        <nav className="flex-1">
          {navItems.map((item) => {
            const Icon = item.icon;
            const isActive = location.pathname === item.path;
            return (
              <Link
                key={item.path}
                to={item.path}
                className={`flex items-center gap-3 px-4 py-3 rounded-lg mb-1 transition-all duration-200 group ${
                  isActive
                    ? "bg-gradient-to-r from-cyan-500/10 to-blue-600/10 border border-cyan-400/20 text-cyan-400 shadow-lg shadow-cyan-500/10"
                    : "text-gray-400 hover:bg-gray-800/50 hover:text-white hover:border-cyan-400/10"
                }`}
              >
                <Icon
                  className={`w-5 h-5 transition-colors duration-200 ${
                    isActive ? "text-cyan-400" : "group-hover:text-cyan-400"
                  }`}
                />
                <span className="font-medium">{item.label}</span>
                {isActive && (
                  <div className="ml-auto w-1.5 h-1.5 rounded-full bg-cyan-400 shadow-lg shadow-cyan-400/50" />
                )}
              </Link>
            );
          })}
        </nav>

        {/* Footer */}
        <div className="text-xs text-gray-500 pt-2 border-t border-gray-800/50">
          <div className="flex items-center justify-between mb-2">
            <div>
              <span className="text-cyan-400">✦</span> Powered by Groq AI
              <br />
              <span className="text-gray-600">v1.0.0</span>
            </div>
            <button
              onClick={handleLogout}
              className="p-2 rounded-lg hover:bg-gray-800/50 text-gray-400 hover:text-white transition-all duration-200"
              title="Logout"
            >
              <LogOut className="w-4 h-4" />
            </button>
          </div>

          {/* Footer Links */}
          <div className="flex justify-center gap-6 text-sm text-gray-500 pt-3 border-t border-gray-800/30">
            <Link
              to="/terms"
              className="hover:text-gray-300 transition-colors duration-200"
            >
              Terms
            </Link>
            <Link
              to="/privacy"
              className="hover:text-gray-300 transition-colors duration-200"
            >
              Privacy
            </Link>
            <Link
              to="/contact"
              className="hover:text-gray-300 transition-colors duration-200"
            >
              Contact
            </Link>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 flex flex-col">
        <header className="bg-gradient-to-r from-gray-900/80 to-gray-950/80 backdrop-blur-sm border-b border-gray-800/50 px-8 py-4">
          <h1 className="text-xl font-semibold bg-gradient-to-r from-white to-gray-300 bg-clip-text text-transparent">
            FrankTech Intelligence
          </h1>
          <p className="text-sm text-gray-400 flex items-center gap-2">
            <span className="w-1.5 h-1.5 rounded-full bg-cyan-400 animate-pulse" />
            AI-powered error monitoring
          </p>
        </header>

        <main className="flex-1 p-8 overflow-auto">{children}</main>
      </div>
    </div>
  );
}

export default Layout;
